import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

interface VideoPlayerProps {
  src: string;
  label?: string;
}

const VideoPlayer = ({ src, label }: VideoPlayerProps) => {
  const [open, setOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Stop & reset video whenever the modal closes
  useEffect(() => {
    if (!open) {
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    }
  }, [open]);

  if (!src) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px 16px",
          backgroundColor: "#f5f5f5",
          borderRadius: 2,
          border: "1px dashed #ccc",
          gap: 0.5,
        }}
      >
        <VideocamOffIcon sx={{ fontSize: 32, color: "#bdbdbd" }} />
        <Typography variant="body2" color="textSecondary" fontSize={12}>
          No video uploaded
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* ── Preview card ── */}
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          width: "100%",
          maxWidth: 220,
          aspectRatio: "16/9",
          borderRadius: 2,
          background:
            "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          cursor: "pointer",
          transition: "transform 0.15s, box-shadow 0.15s",
          "&:hover": {
            transform: "scale(1.03)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
          },
          "&:hover .play-icon": {
            color: "#fff",
            transform: "scale(1.15)",
          },
        }}
      >
        <PlayCircleOutlineIcon
          className="play-icon"
          sx={{
            fontSize: 48,
            color: "rgba(255,255,255,0.75)",
            transition: "color 0.15s, transform 0.15s",
          }}
        />
        <Button
          variant="text"
          size="small"
          disableRipple
          sx={{
            color: "rgba(255,255,255,0.85)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            pointerEvents: "none",
            p: 0,
          }}
        >
          Preview
        </Button>
      </Box>

      {/* ── Video modal ── */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
            backgroundColor: "#1a1a2e",
            color: "#fff",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} fontSize={14}>
            {label ? `Video Preview — ${label}` : "Video Preview"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: "#000" }}>
          <video
            key={src}
            ref={videoRef}
            src={src}
            autoPlay
            controls
            playsInline
            style={{ width: "100%", display: "block", maxHeight: "70vh" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoPlayer;
